import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, LoaderCircle, Save } from "lucide-react";
import { useState } from "react";
import { type UseFormSetError, useForm } from "react-hook-form";
import { z } from "zod";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { FieldError } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ApiError } from "@/lib/api";

// Espejo de la validación de servidor (obras/dto.go): nombre obligatorio ≤120,
// descripción ≤500. El `codigo_obra` lo asigna el servidor (correlativo), no viaja
// en el formulario.
const obraSchema = z.object({
  nombre: z.string().trim().min(1, "El nombre es obligatorio").max(120, "Máximo 120 caracteres"),
  descripcion: z.string().trim().max(500, "Máximo 500 caracteres"),
  activa: z.boolean(),
});

export type ObraFormValues = z.infer<typeof obraSchema>;

function aplicarError(
  err: unknown,
  setError: UseFormSetError<ObraFormValues>,
  setFormError: (msg: string) => void,
): void {
  if (err instanceof ApiError) {
    if (err.fields) {
      if (err.fields.nombre) {
        setError("nombre", { message: err.fields.nombre });
      }
      if (err.fields.descripcion) {
        setError("descripcion", { message: err.fields.descripcion });
      }
      if (err.fields.nombre || err.fields.descripcion) {
        return;
      }
    }
    setFormError(err.message || "No se pudo guardar la obra.");
    return;
  }
  setFormError("No se pudo guardar la obra. Comprueba tu conexión.");
}

/**
 * ObraForm es el formulario reutilizable de alta/edición de obras (react-hook-form +
 * zod): nombre, descripción opcional y estado activo. Mapea los errores por campo del
 * backend (VALIDACION). Sigue el patrón de AreaForm (#28).
 */
export function ObraForm({
  defaultValues,
  submitLabel,
  pending,
  onSubmit,
  onCancel,
}: {
  defaultValues: ObraFormValues;
  submitLabel: string;
  pending: boolean;
  onSubmit: (values: ObraFormValues) => Promise<void>;
  onCancel: () => void;
}) {
  const [formError, setFormError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<ObraFormValues>({ resolver: zodResolver(obraSchema), defaultValues });

  const submit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await onSubmit(values);
    } catch (err) {
      aplicarError(err, setError, setFormError);
    }
  });

  return (
    <form className="flex flex-col gap-5" onSubmit={submit} noValidate>
      {formError ? (
        <Alert variant="destructive">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>{formError}</span>
        </Alert>
      ) : null}

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="nombre">Nombre</Label>
        <Input
          id="nombre"
          autoComplete="off"
          aria-invalid={errors.nombre ? true : undefined}
          {...register("nombre")}
        />
        <FieldError>{errors.nombre?.message}</FieldError>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="descripcion">Descripción (opcional)</Label>
        <Textarea
          id="descripcion"
          aria-invalid={errors.descripcion ? true : undefined}
          {...register("descripcion")}
        />
        <FieldError>{errors.descripcion?.message}</FieldError>
      </div>

      <label className="flex items-center gap-3">
        <input
          type="checkbox"
          className="h-5 w-5 shrink-0 rounded border-slate-300 text-primary-600 focus-visible:ring-2 focus-visible:ring-primary-500"
          {...register("activa")}
        />
        <span className="text-base text-slate-800">Obra activa</span>
      </label>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
          Cancelar
        </Button>
        <Button type="submit" disabled={pending}>
          {pending ? (
            <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
          ) : (
            <Save className="h-5 w-5" aria-hidden="true" />
          )}
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
